import { useEffect, useState } from "react";
import { auth, db } from "../firebase-config";
import { doc, getDoc, updateDoc, arrayUnion, where, query, collection, getDocs, setDoc } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { logTeamHistory } from './TeacherDashboard.js';
import "../style/Dashboard.css"; 
import { useLocation } from "react-router-dom";

function StudentDashboard() {
    const [joinedClasses, setJoinedClasses] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [inviteCode, setInviteCode] = useState("");
    const [selectedClass, setSelectedClass] = useState(null);
    const [activeTab, setActiveTab] = useState("Overview");
    const [questionnaires, setQuestionnaires] = useState({});
    const [isQuestionnaireStarted, setIsQuestionnaireStarted] = useState(false);
    const [currentQuestionnaire, setCurrentQuestionnaire] = useState(null);
    const [hasCompleted, setHasCompleted] = useState(false);
    const [completedQuestionnaires, setCompletedQuestionnaires] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [responses, setResponses] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [error, setError] = useState("");
    const [groupMembers, setGroupMembers] = useState([]);
    const [hasTeam, setHasTeam] = useState(false);
    const [feedbackText, setFeedbackText] = useState("");
    const [feedbackSubmitted, setFeedbackSubmitted] = useState([]);
    const [teamAllocationFeedback, setTeamAllocationFeedback] = useState(null);
    const [teamFeedbackText, setTeamFeedbackText] = useState("");
    const [teamFeedbackSubmitted, setTeamFeedbackSubmitted] = useState(false);
    const [teamNotification, setTeamNotification] = useState(null);
    const [isAcceptingTeam, setIsAcceptingTeam] = useState(false);
    const [isCheckingCompletion, setIsCheckingCompletion] = useState(false);
    const [showQuestionnaireContent, setShowQuestionnaireContent] = useState(false);
    const location = useLocation();
    const classIdFromNav = location.state?.classId;

    useEffect(() => {    
        fetchStudentData();

        // Check for team allocation feedback if a class is already selected
        if (selectedClass) {
            checkTeamAllocationFeedback(selectedClass.classId);
        }
    }, []);

    const fetchStudentData = async () => {
        try {
            const user = auth.currentUser;
            if (!user) return;
    
            const userDocRef = doc(db, "Users", user.uid);
            const userDoc = await getDoc(userDocRef);
    
            if (userDoc.exists()) {
                const userData = userDoc.data();
                const userClasses = userData.joinedClasses || [];
                setTeamNotification(userData.teamNotification || null);
                
                // First get all class IDs to check their archived status
                const classIds = userClasses
                    .map(c => c.classId)
                    .filter(id => id && typeof id === 'string');
    
                if (classIds.length === 0) {
                    setJoinedClasses([]);
                    setQuestionnaires({});
                    return;
                }
    
                // Fetch all classes to check archived status
                const classesQuery = query(
                    collection(db, "Classes"),
                    where("classId", "in", classIds)
                );
                const classesSnapshot = await getDocs(classesQuery);
                
                // Create a map of classId to isArchived status
                const classStatusMap = {};
                classesSnapshot.forEach(doc => {
                    const classData = doc.data();
                    classStatusMap[classData.classId] = classData.isArchived || false;
                });
    
                // Filter out archived classes
                const nonArchivedClasses = userClasses.filter(c => !classStatusMap[c.classId]);
                setJoinedClasses(nonArchivedClasses);

                if (classIdFromNav) {
                    const targetClass = nonArchivedClasses.find(c => c.classId === classIdFromNav);
                    if (targetClass) {
                        handleClassClick(targetClass);
                    }
                    else if (selectedClass && selectedClass.classId === classIdFromNav) {
                        setSelectedClass(null);
                        setCurrentQuestionnaire(null);
                        setQuestions([]);
                        setGroupMembers([]);
                        setHasTeam(false);
                    }
                    
                }
    
                // Now fetch questionnaires only for non-archived classes
                const nonArchivedClassIds = nonArchivedClasses.map(c => c.classId);
                
                if (nonArchivedClassIds.length === 0) {
                    setQuestionnaires({});
                    return;
                }
    
                const q = query(
                    collection(db, "Questionnaires"),
                    where("classId", "in", nonArchivedClassIds)
                );
                const querySnapshot = await getDocs(q);
    
                const allQuestionnaires = querySnapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        ...data,
                        status: data.status || "inactive",
                        questionnaireId: doc.id,
                        ref: doc.ref,
                    };
                });
    
                const now = new Date();
                const visibleQuestionnaires = [];
    
                for (const q of allQuestionnaires) {
                    const startDate = q.startDate?.toDate?.() || new Date(q.startDate);
                    const endDate = q.endDate?.toDate?.() || new Date(q.endDate);
    
                    if (q.status === "inactive" && startDate <= now) {
                        await updateDoc(q.ref, { status: "active" });
                        q.status = "active";
                    }
    
                    if (q.status === "active" && endDate < now) {
                        await updateDoc(q.ref, { status: "inactive" });
                        q.status = "inactive";
                    }
    
                    const isWithinDateRange = startDate <= now && now <= endDate;
                    const isCompleted = await checkIfCompleted(q.questionnaireId, user.uid);
    
                    if (q.status === "active" && isWithinDateRange && !isCompleted) {
                        visibleQuestionnaires.push(q);
                    }
                }
    
                // Group visible questionnaires by className
                const questionnairesByClass = {};
                for (const q of visibleQuestionnaires) {
                    if (!questionnairesByClass[q.classId]) {
                        questionnairesByClass[q.classId] = [];
                    }
                    questionnairesByClass[q.classId].push(q);
                }
                setQuestionnaires(questionnairesByClass);
            }
        } catch (error) {
            console.error("Error fetching student data:", error);
        }
    };

    const fetchQuestionnairesForClass = async (classId) => {
        try {
            const q = query(
                collection(db, "Questionnaires"),
                where("classId", "==", classId)
            );
            const querySnapshot = await getDocs(q);
    
            const now = new Date();
            const classQuestionnaires = [];
    
            for (const docSnap of querySnapshot.docs) {
                const data = docSnap.data();
                const startDate = data.startDate?.toDate?.() || new Date(data.startDate);
                const endDate = data.endDate?.toDate?.() || new Date(data.endDate);
    
                if (data.status === "inactive" && startDate <= now) {
                    await updateDoc(docSnap.ref, { status: "active" });
                    data.status = "active";
                }
                if (data.status === "active" && endDate < now) {
                    await updateDoc(docSnap.ref, { status: "inactive" });
                    data.status = "inactive";
                }
    
                const isWithinDateRange = startDate <= now && now <= endDate;

                if (isWithinDateRange) {
                    classQuestionnaires.push({
                      ...data,
                      questionnaireId: docSnap.id,
                      ref: docSnap.ref
                    });
                }
            }
            return classQuestionnaires;
        } catch (error) {
            console.error("Error fetching questionnaires for class:", error);
            return [];
        }
    };    

    const fetchQuestions = async (questionnaireId) => {
        const questionnaireDoc = await getDoc(doc(db, "Questionnaires", questionnaireId));
        if (questionnaireDoc.exists()) {
            setQuestions(questionnaireDoc.data().questions || []);
        }
    };

    const checkIfCompleted = async (questionnaireId, uid) => {
        const responseDoc = await getDoc(doc(db, "Responses", questionnaireId));
        
        if (!responseDoc.exists()) return false;
        
        const responses = responseDoc.data().responses || [];
        return responses.some(response => response.uid === uid);
    };

    const hasUncompletedQuestionnaires = (classId) => {
        if (!classId) return false;
        
        const classQuestionnaires = questionnaires[classId] || [];
        const now = new Date();
        
        return classQuestionnaires.some(q => {
            const startDate = q.startDate?.toDate?.() || new Date(q.startDate);
            const endDate = q.endDate?.toDate?.() || new Date(q.endDate);
            const isWithinDateRange = startDate <= now && now <= endDate;
            
            return isWithinDateRange && !completedQuestionnaires.includes(q.questionnaireId);
        });
    };

    const checkTeamAllocationFeedback = async (classId) => {
        try {
            const user = auth.currentUser;
            if (!user) return false;

            const now = new Date();
            const q = query(
                collection(db, "Questionnaires"),
                where("classId", "==", classId),
                where("enableTeamAllocationFeedback", "==", true)
            );
            
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                // Filter client-side for date range
                const activeFeedback = querySnapshot.docs.find(doc => {
                    const data = doc.data();
                    const start = data.teamAllocationFeedbackStart?.toDate?.() || new Date(data.teamAllocationFeedbackStart);
                    const end = data.teamAllocationFeedbackEnd?.toDate?.() || new Date(data.teamAllocationFeedbackEnd);
                    return start <= now && now <= end;
                });

                if (activeFeedback) {
                    const feedbackData = activeFeedback.data();
                    
                    // Check if user has already submitted feedback
                    const feedbackRef = doc(db, "Feedback", feedbackData.questionnaireId);
                    const feedbackDoc = await getDoc(feedbackRef);
                    
                    if (feedbackDoc.exists()) {
                        const existingFeedback = feedbackDoc.data().teamAllocationFeedback || [];
                        const hasSubmitted = existingFeedback.some(fb => fb.uid === user.uid);
                        setTeamFeedbackSubmitted(hasSubmitted);
                    } else {
                        setTeamFeedbackSubmitted(false);
                    }

                    setTeamAllocationFeedback(feedbackData);
                    return true;
                }
            }
            return false;
        } catch (error) {
            console.error("Error checking team allocation feedback:", error);
            return false;
        }
    };

    const fetchTeamMembers = async (currentClass) => {
        const user = auth.currentUser;
        if (!user || !currentClass) {
            setGroupMembers([]);
            setHasTeam(false);
            return;
        }
    
        try {
            // 1. Get the class document from Classes collection
            const classDocRef = doc(db, "Classes", currentClass.classId);
            const classDoc = await getDoc(classDocRef);
            
            if (!classDoc.exists()) {
                setGroupMembers([]);
                setHasTeam(false);
                return;
            }
            
            const classData = classDoc.data();
            
            // Check if class is archived
            if (classData.isArchived) {
                setGroupMembers([]);
                setHasTeam(false);
                return;
            }
    
            // Rest of the function remains the same...
            // 2. Check if students array exists
            if (!classData.students || !Array.isArray(classData.students)) {
                setGroupMembers([]);
                setHasTeam(false);
                return;
            }
            // 3. Find current user in students array
            const currentStudent = classData.students.find(s => s.uid === user.uid);
            if (!currentStudent || !currentStudent.teamNumber) {
                setGroupMembers([]);
                setHasTeam(false);
                return;
            }
            // 4. Find all teammates (excluding current user)
            const teammates = classData.students.filter(
                s => s.teamNumber === currentStudent.teamNumber
            );
            setGroupMembers(teammates);
            setHasTeam(teammates.length > 0);
            
        } catch (error) {
            console.error("Error fetching team members:", error);
            setGroupMembers([]);
            setHasTeam(false);
        }
    };

    const handleClassClick = async (classData) => {
        setActiveTab("Overview");
        setSelectedClass(classData);
        setTeamAllocationFeedback(null);
        setShowQuestionnaireContent(false); // Hide content while loading
        setIsCheckingCompletion(true); // Start loading

        const classQuestionnaires = await fetchQuestionnairesForClass(classData.classId);
        setQuestionnaires(prev => ({ ...prev, [classData.classId]: classQuestionnaires }));

        await checkTeamAllocationFeedback(classData.classId);

        if (classQuestionnaires.length > 0) {
            const firstQ = classQuestionnaires[0];
            setCurrentQuestionnaire(firstQ);

            const completed = await checkIfCompleted(firstQ.questionnaireId, auth.currentUser.uid);
            setHasCompleted(completed);
            
            if (!completed) {
                await fetchQuestions(firstQ.questionnaireId);
            } else {
                await checkExistingFeedback(firstQ.questionnaireId);
            }
        } else {
            setCurrentQuestionnaire(null);
            setHasCompleted(false);
        }

        await fetchTeamMembers(classData);
        setIsCheckingCompletion(false); // Done loading
        setShowQuestionnaireContent(true); // Now safe to show content
    };
    
    
    const handleAcceptGroup = async () => {
        if (isAcceptingTeam) return; 
        setIsAcceptingTeam(true);

        const user = auth.currentUser;
        if (!user || !selectedClass) {
            setIsAcceptingTeam(false);
            return;
        }

        try {
            // 1. Get the class document reference
            const classDocRef = doc(db, "Classes", selectedClass.classId);
            const classDoc = await getDoc(classDocRef);

            if (!classDoc.exists()) {
                alert("Class document not found");
                return;
            }

            // 2. Get the user document for full name
            const userDocRef = doc(db, "Users", user.uid);
            const userDoc = await getDoc(userDocRef);
            
            if (!userDoc.exists()) {
                alert("User document not found");
                return;
            }
            
            const userData = userDoc.data();
            const studentName = `${userData.firstName} ${userData.lastName}`;

            // 3. Update the student's hasConfirmedGroup status
            const students = classDoc.data().students || [];
            const currentStudent = students.find(student => student.uid === user.uid);
            
            if (!currentStudent) {
                alert("Student not found in class");
                return;
            }

            const updatedStudents = students.map(student => {
                if (student.uid === user.uid) {
                    return { 
                        ...student, 
                        hasConfirmedGroup: true 
                    };
                }
                return student;
            });

            // 4. Save the updated students array
            await updateDoc(classDocRef, { students: updatedStudents });

            // 5. Log this action to history
            await logTeamHistory(
                selectedClass.classId,
                'accepted',
                user.uid,
                studentName,
                currentStudent.teamNumber
            );
            
            // 6. Notify teammates that the student accepted the group
            const className = classDoc.data()?.className || "your class";
            const notifyTeammatesOfAcceptance = async (teamNumber, accepter, teammates) => {
                for (const member of teammates) {
                    if (member.uid === user.uid) continue; // skip self
                    try {
                        await pushNotification(
                            member.uid,
                            `${accepter} has accepted the team assignment in ${className}`
                        );
                    } catch (error) {
                        console.error(`Failed to notify teammate ${member.uid}:`, error);
                    }
                }
            };

            if (currentStudent?.teamNumber && groupMembers.length > 0) {
                await notifyTeammatesOfAcceptance(
                    currentStudent.teamNumber,
                    studentName,
                    groupMembers
                );
            }

            // Notify student
            await pushNotification(
                user.uid,
                `You have accepted your team (Team ${currentStudent.teamNumber}) in ${selectedClass.name}`
            );

            // Notify teacher if teacherId exists
            const teacherId = classDoc.data()?.teacherId; // Get from fresh doc
            if (teacherId) {  // Only send if we have a valid teacher ID
                try {
                    await pushNotification(
                        teacherId,
                        `${studentName} accepted the team (Team ${currentStudent.teamNumber}) in ${selectedClass.name}`
                    );
                } catch (error) {
                    console.error("Failed to notify teacher:", error);
                }
            }

            // 7. Update local state and show success
            fetchTeamMembers(selectedClass);
            alert("You have successfully accepted your team assignment.");
            
        } catch (error) {
            console.error("Error accepting group:", error);
            alert("Failed to accept group. Please try again.");
        } finally {
            setIsAcceptingTeam(false);
        }
    };

    const handleLeaveGroup = async () => {
        const user = auth.currentUser;
        if (!user || !selectedClass) return;
    
        try {
            // 1. Get the class document reference
            const classDocRef = doc(db, "Classes", selectedClass.classId);
            const classDoc = await getDoc(classDocRef);
    
            if (!classDoc.exists()) {
                throw new Error("Class document does not exist");
            }
    
            // 2. Get the user document for full name
            const userDocRef = doc(db, "Users", user.uid);
            const userDoc = await getDoc(userDocRef);
            const userData = userDoc.data();
            const studentName = `${userData.firstName} ${userData.lastName}`;
            
            // 3. Find previous team before modifying anything
            const teamDocRef = doc(db, "Teams", selectedClass.classId);
            const teamDoc = await getDoc(teamDocRef);
            let previousTeamNumber = null;
            let originalTeamMembers = [];

            if (teamDoc.exists()) {
                const teamList = teamDoc.data().teams || [];
                const currentTeam = teamList.find(t => t.students.some(s => s.uid === user.uid));
                previousTeamNumber = currentTeam?.teamNumber || null;
                originalTeamMembers = currentTeam?.students || [];
            }

            // 4. Notify teammates BEFORE removing student
            const className = classDoc.data()?.className || "your class";

            const notifyTeamMembersOfDeparture = async (teamNumber, leaver, teammates) => {
                for (const member of teammates) {
                    if (member.uid === user.uid) continue; // skip self
                    const memberRef = doc(db, "Users", member.uid);
                    await updateDoc(memberRef, {
                        notifications: arrayUnion({
                            message: `${leaver} has left your team in ${className}`,
                            timestamp: new Date(),
                            seen: false
                        })
                    });
                }
            };
            if (previousTeamNumber && originalTeamMembers.length > 0) {
                await notifyTeamMembersOfDeparture(previousTeamNumber, studentName, originalTeamMembers);
            }
    
            // 5. Update class doc: remove team info from student
            const students = classDoc.data().students;
            const updatedStudents = students.map(student => {
                if (student.uid === user.uid) {
                    return {
                        ...student,
                        teamNumber: 'N/A',
                        hasConfirmedGroup: false
                    };
                }
                return student;
            });
            await updateDoc(classDocRef, { students: updatedStudents });

            // 6. Remove student from team in Teams doc
            if (teamDoc.exists()) {
                const updatedTeams = teamDoc.data().teams.map(team => ({
                    ...team,
                    students: team.students.filter(s => s.uid !== user.uid)
                }));
                await updateDoc(teamDocRef, {
                    teams: updatedTeams,
                    lastUpdated: new Date()
                });
            }

            // 7. Log history
            if (previousTeamNumber) {
                await logTeamHistory(
                    selectedClass.classId,
                    'left',
                    user.uid,
                    studentName,
                    previousTeamNumber
                );
            }

            // 8. Notify student themself and teacher
            await pushNotification(  // To student
                user.uid,
                `You have left your team (Team ${previousTeamNumber}) in ${selectedClass.name}`
            );

            const teacherId = classDoc.data()?.teacherId;
            if (teacherId) {  // Only send if we have a valid teacher ID
                await pushNotification(
                    teacherId,
                    `${studentName} left the team (Team ${previousTeamNumber}) in ${selectedClass.name}`
                );
            }   

            // 9. Update UI
            fetchTeamMembers(selectedClass);
            alert("You have successfully left the team.");
    
        } catch (error) {
            console.error("Error leaving team:", error);
            alert("Failed to leave team. Please try again.");
        }
    };
    
    const joinClass = async () => {
        setError(""); // Clear previous errors
        if (!inviteCode.trim()) {
            setError("Please enter an invite code.");
            return;
        }
        try {
            // Check if class with invite code exists
            const q = query(collection(db, "Classes"), where("inviteCode", "==", inviteCode));
            const querySnapshot = await getDocs(q);
    
            if (querySnapshot.empty) {
                setError("Invalid invite code. Please try again.");
                return;
            }
    
            const classDoc = querySnapshot.docs[0];
            const classData = classDoc.data();
            
            // Check if class is archived
            if (classData.isArchived) {
                setError("This class has been archived and cannot be joined.");
                return;
            }
    
            const classDocRef = classDoc.ref;
            const user = auth.currentUser;
    
            if (!user) {
                setError("User is not authenticated.");
                return;
            }
    
            const userDocRef = doc(db, "Users", user.uid);
            const userDoc = await getDoc(userDocRef);
            const userData = userDoc.data();
    
            // Check if student has already joined the class
            const alreadyJoined = userData.joinedClasses?.some(c => c.inviteCode === inviteCode);
    
            if (alreadyJoined) {
                setError("You have already joined this class.");
                return;
            }
            const fullName = `${userData.firstName} ${userData.lastName}`;
    
            // Update user document (enroll student in class)
            await updateDoc(userDocRef, {
                joinedClasses: arrayUnion({ 
                    name: classData.className.trim(), 
                    inviteCode, 
                    classId: classData.classId 
                })
            });
    
            // Update class document (add student to class)
            await updateDoc(classDocRef, {
                students: arrayUnion({ uid: user.uid, name: fullName })
            });

            setJoinedClasses([...joinedClasses, { 
                name: classData.className.trim(), 
                inviteCode, 
                classId: classData.classId 
            }]); // Update local state
            setInviteCode(""); // Clear input field
    
            // Fetch questionnaires linked to the class
            await fetchStudentData();
            await pushNotification(user.uid, `You have joined the class ${classData.className}`);      // To student

            const teacherId = classData.teacherId;
            await pushNotification(teacherId, `${fullName} joined the class ${classData.className}`);  // To teacher
        } catch (error) {
            setError("An error occurred. Please try again.");
        }
    };

    const handleStartQuestionnaire = () => {
        setIsQuestionnaireStarted(true);
        setCurrentQuestionIndex(0); // Reset to the first question
    };

    const handleResponseChange = (questionId, option) => {
        const updatedResponses = {
            ...responses,
            [questionId]: option,
        };
        setResponses(updatedResponses);

        const allAnswered = questions.every((_, index) => updatedResponses[index] !== undefined);
        setIsFormValid(allAnswered);
    };

    const handleSubmit = async () => {
        if (!isFormValid) {
            setError("Please answer all questions before submitting.");
            return;
        }
    
        const user = auth.currentUser;
        if (!user) return;
    
        try {
            const responseRef = doc(db, "Responses", currentQuestionnaire.questionnaireId);
            
            // Use setDoc with merge: true to create or update the document
            await setDoc(responseRef, {
                responses: arrayUnion({
                    uid: user.uid,
                    answers: responses,
                    timestamp: new Date(),
                    responseId: uuidv4() // Unique ID for each response
                })
            }, { merge: true }); // This creates the document if it doesn't exist
    
            // Store feedback separately if provided
            if (feedbackText) {
                await setDoc(responseRef, {
                    feedback: arrayUnion({
                        uid: user.uid,
                        text: feedbackText,
                        timestamp: new Date()
                    })
                }, { merge: true });
            }
    
            // Mark as completed in user's profile
            const userDocRef = doc(db, "Users", user.uid);
            const userDoc = await getDoc(userDocRef);
            const userData = userDoc.data();
            const fullName = `${userData.firstName} ${userData.lastName}`;
            await updateDoc(userDocRef, {
                completedQuestionnaires: arrayUnion(currentQuestionnaire.questionnaireId),
            });
            
            const classRef = doc(db, "Classes", currentQuestionnaire.classId);
            const classSnap = await getDoc(classRef);
            const className = classSnap.exists() ? (classSnap.data().className || "your class") : "your class";
            const teacherId = classSnap.exists() ? classSnap.data().teacherId : null;

            await pushNotification(  // To student
                user.uid,
                `You have submitted the questionnaire for ${className}: ${currentQuestionnaire.title}`
            );
            await pushNotification(  // To teacher
                teacherId,
                `${fullName} submitted the questionnaire "${currentQuestionnaire.title}" in ${className}`
            );
    
            setCompletedQuestionnaires(prev => [...prev, currentQuestionnaire.questionnaireId]);
            setHasCompleted(true);
            setIsQuestionnaireStarted(false);
            setResponses({});
            alert("Responses submitted successfully!");
        } catch (error) {
            console.error("Error submitting responses:", error);
            alert("Failed to submit responses.");
        }
    };

    const checkExistingFeedback = async (questionnaireId) => {
        try {
            const user = auth.currentUser;
            if (!user) return false;

            const feedbackRef = doc(db, "Feedback", questionnaireId);
            const feedbackDoc = await getDoc(feedbackRef);
            
            if (feedbackDoc.exists()) {
                const feedbackData = feedbackDoc.data().questionnaireFeedback || [];
                const hasSubmitted = feedbackData.some(fb => fb.uid === user.uid);
                
                if (hasSubmitted) {
                    setFeedbackSubmitted(prev => [...prev, questionnaireId]);
                    return true;
                }
            }
            return false;
        } catch (error) {
            console.error("Error checking existing feedback:", error);
            return false;
        }
    };
    
    const handleQuestionnaireFeedbackSubmit = async () => {
        try {
            if (!feedbackText.trim()) {
                alert("Please enter your feedback before submitting");
                return;
            }
            
            const user = auth.currentUser;
            if (!user || !currentQuestionnaire) return;
            
            // Check if feedback already submitted for this questionnaire
            if (feedbackSubmitted.includes(currentQuestionnaire.questionnaireId)) {
                alert("You've already submitted feedback for this questionnaire");
                return;
            }

            // Catch user data for notification
            const userDoc = await getDoc(doc(db, "Users", user.uid));
            const userData = userDoc.data();
            const fullName = `${userData.firstName} ${userData.lastName}`;
      
            const feedbackRef = doc(db, "Feedback", currentQuestionnaire.questionnaireId);
        
            await setDoc(feedbackRef, {
                questionnaireFeedback: arrayUnion({
                uid: user.uid,
                text: feedbackText,
                timestamp: new Date()
                })
            }, { merge: true });
        
            setFeedbackSubmitted(prev => [...prev, currentQuestionnaire.questionnaireId]);
            setFeedbackText("");
            await pushNotification(auth.currentUser.uid, "Your feedback has been submitted.");  // To student

            const classRef = doc(db, "Classes", currentQuestionnaire.classId);
            const classSnap = await getDoc(classRef);
            const className = classSnap.exists() ? (classSnap.data().className || "your class") : "your class";
            const teacherId = classSnap.exists() ? classSnap.data().teacherId : null;
            await pushNotification(  // To teacher
                teacherId,
                `${fullName} submitted feedback for "${currentQuestionnaire.title}" in ${className}`
            );
        } catch (err) {
            console.error("Failed to submit feedback:", err);
            alert("Submission failed. Please try again.");
        }
    };
    
    const handleTeamFeedbackSubmit = async () => {
        try {
            const user = auth.currentUser;
            if (!user || !teamAllocationFeedback || teamFeedbackSubmitted) return;

            const feedbackRef = doc(db, "Feedback", teamAllocationFeedback.questionnaireId);
        
            await setDoc(feedbackRef, {
                teamAllocationFeedback: arrayUnion({
                    uid: user.uid,
                    text: teamFeedbackText,
                    timestamp: new Date(),
                })
            }, { merge: true });

            setTeamFeedbackSubmitted(true);
            setTeamFeedbackText("");
            alert("Thank you for your team feedback!");
        } catch (err) {
            console.error("Failed to submit team feedback:", err);
            alert("Submission failed. Please try again.");
        }
    };

    const renderQuestionnaire = () => {
        const currentQuestion = questions[currentQuestionIndex];
    
        return (
            <div>
                <h3 className="questionnaire-title">Questionnaire: {currentQuestionnaire?.title}</h3>
                <form onSubmit={(e) => e.preventDefault()}>
                    <div key={currentQuestionIndex} className="question-card">
                        {/* Question number, put at the top */}
                        <p className="question-meta">Question {currentQuestionIndex + 1} of {questions.length}</p>

                        {/* Question text */}
                        <label className="question-text">
                            {currentQuestion.text.charAt(0).toUpperCase() + currentQuestion.text.slice(1)}  {/* Capitalise the first letter */}
                        </label>
                        {currentQuestion.options.map((option, optIndex) => (
                            <div key={optIndex} className="otion-text">
                                <input
                                    type="radio"
                                    name={`question-${currentQuestionIndex}`}
                                    value={option}
                                    onChange={() => handleResponseChange(currentQuestionIndex, option)}
                                    checked={responses[currentQuestionIndex] === option}
                                />
                                {option}
                            </div>
                        ))}
                    </div>
    
                    {/* Navigation Buttons */}
                    <div className="nav-buttons-container">
                        {/* Previous Button, only displayed when it is not the first question */}
                        {currentQuestionIndex > 0 && (
                            <button
                                type="button"
                                className="nav-button"
                                onClick={() => setCurrentQuestionIndex(prev => Math.max(prev - 1, 0))}
                            >
                                Previous
                            </button>
                        )}
                        {/* Next Button, only displayed when it is not the last question */}
                        {currentQuestionIndex < questions.length - 1 && (
                            <button
                                type="button"
                                className="nav-button"
                                onClick={() => setCurrentQuestionIndex(prev => Math.min(prev + 1, questions.length - 1))}
                            >
                                Next
                            </button>
                        )}
                    </div>
    
                    {/* Submit Button, only shown on the last question */}
                    {currentQuestionIndex === questions.length - 1 && (
                        <button
                            type="button"
                            className="submit-button"
                            onClick={handleSubmit}
                            disabled={!isFormValid}
                        >
                            Submit
                        </button>
                    )}
                </form>
            </div>
        );
    };

    const pushNotification = async (userId, message) => {
        if (!userId || typeof userId !== 'string') {
            console.error("Invalid user ID:", userId);
            return;
        }

        try {
            const userRef = doc(db, "Users", userId);
            await updateDoc(userRef, {
                notifications: arrayUnion({
                    message,
                    timestamp: new Date(),
                    seen: false
                })
            });
        } catch (error) {
            console.error(`Failed to send notification to user ${userId}:`, error);
            throw error; // Re-throw to let calling function handle it
        }
    };

    return (
        <div className="dashboard-container">
            {/* Left Sidebar - Class List */}
            <div className="sidebar">
            <div className="sidebar-inner">
                <input 
                    type="text" 
                    placeholder="Search for a class..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <input
                    type="text"
                    placeholder="Enter invite code"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                />
                <button onClick={joinClass}>Join Class</button>
                {error && <p>{error}</p>}
            </div>
                <ul>
                    {joinedClasses
                        .filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
                        .map((c, index) => (
                            <li 
                                key={index} 
                                className={selectedClass?.inviteCode === c.inviteCode ? "selected" : ""}
                                onClick={() => handleClassClick(c)}
                            >
                                {c.name}
                            </li>
                        ))}
                </ul>
        </div>
        
        {/* Right Panel - Class Overview */}
        <div className="class-content">
            {selectedClass ? (
                <>
                    <h2>Dashboard</h2>
                    <div className="subnavbar">
                        {["Overview", "Tasks", "Documents", "Group Status"].map(tab => (
                            <button 
                                key={tab}
                                className={activeTab === tab ? "active" : ""}
                                onClick={() => setActiveTab(tab)}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                    <div className="tab-content">
                        {activeTab === "Overview" && (
                            <div>
                                {isCheckingCompletion ? (
                                    <p>Loading questionnaire status...</p> // Show loading indicator
                                ) : showQuestionnaireContent && currentQuestionnaire ? (
                                    !hasCompleted ? (
                                        isQuestionnaireStarted ? (
                                            renderQuestionnaire()
                                        ) : (
                                            <button className="start-btn" onClick={handleStartQuestionnaire}>Start Questionnaire</button>
                                        )
                                    ) : (
                                        <div>
                                            {!feedbackSubmitted.includes(currentQuestionnaire.questionnaireId) ? (
                                                <div className="feedback-section">
                                                    <h4>Please leave your feedback:</h4>
                                                    <textarea
                                                        rows="4"
                                                        cols="50"
                                                        value={feedbackText}
                                                        onChange={(e) => setFeedbackText(e.target.value)}
                                                        placeholder="Share your thoughts about the questionnaire..."
                                                    />
                                                    <button
                                                        className="feedback-button"
                                                        onClick={handleQuestionnaireFeedbackSubmit}
                                                    >
                                                        Submit Feedback
                                                    </button>
                                                </div>
                                            ) : (
                                                <p>Thank you for completing this questionnaire and providing your feedback.</p>
                                            )}
                                        </div>
                                    )
                                ) : (
                                    <p>All up to date!</p>
                                )}
                            </div>
                        )}
                        {activeTab === "Tasks" && <div>Tasks Content</div>}
                        {activeTab === "Documents" && <div>Documents Content</div>}
                        {activeTab === "Group Status" && (
                            <div>
                                {hasUncompletedQuestionnaires(selectedClass?.classId) ? (
                                    <p>Grouping will start after the questionnaire period ends.</p>
                                ) : groupMembers.length > 0 && groupMembers[0].teamNumber !== 'N/A' ? (
                                    <div>
                                        <p>Team {groupMembers[0].teamNumber}</p>
                                        <ul className="group-members-list">
                                            {groupMembers.map((member, idx) => (
                                                <li key={idx} className="group-member-item">
                                                    <span>{member.name}</span>
                                                    {member.hasConfirmedGroup ? (
                                                        <span className="accepted-status">✓ Accepted</span>
                                                    ) : (
                                                        <span className="pending-status">Pending</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>

                                        {!groupMembers.find(m => m.uid === auth.currentUser?.uid)?.hasConfirmedGroup && (
                                            <div className="group-actions">
                                                <button
                                                    onClick={handleAcceptGroup}
                                                    disabled={groupMembers.find(m => m.uid === auth.currentUser?.uid)?.hasConfirmedGroup || isAcceptingTeam}
                                                    className="accept-team-btn"
                                                >
                                                    {isAcceptingTeam ? "Processing..." :
                                                        (groupMembers.find(m => m.uid === auth.currentUser?.uid)?.hasConfirmedGroup 
                                                            ? "Already Accepted" 
                                                            : "Accept Team")}
                                                </button>
                                                <button
                                                    onClick={handleLeaveGroup}
                                                    className="leave-team-btn"
                                                >
                                                    Leave/Reject Team
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p>You are not assigned to a team yet. Please wait for the teacher to assign you.</p>
                                )}
                                
                                {teamAllocationFeedback && (
                                    <div className="group-status-container">
                                        <h4>Team Allocation Feedback</h4>
                                        {!teamFeedbackSubmitted ? (
                                            <div className="feedback-section">
                                                <textarea
                                                    rows="4"
                                                    cols="50"
                                                    value={teamFeedbackText}
                                                    onChange={(e) => setTeamFeedbackText(e.target.value)}
                                                    placeholder="Share your thoughts about your team allocation..."
                                                    className="group-status-textarea"
                                                />
                                                <button
                                                    onClick={handleTeamFeedbackSubmit}
                                                    disabled={!teamFeedbackText.trim()}
                                                    className="feedback-button"
                                                >
                                                    Submit Team Feedback
                                                </button>
                                            </div>
                                        ) : (
                                            <p className="feedback-success-message">Thank you for submitting your team feedback!</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <p>Select a class to view its dashboard.</p>
            )}
        </div>
    </div>
    );
}

export default StudentDashboard;